import { useState, useMemo, useId } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  getHolidaySet,
  holidayName,
  localDateOf,
  DAY_OF_WEEK_KEYS,
  DAY_OF_WEEK_LABELS,
  guestPriceOf,
} from '../../engine';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface BookingZoneEditorProps {
  /** Price zones to edit (each cloned into a local draft). */
  zones?: any[];
  /** Base price per hour for time not covered by any zone. */
  defaultPricePerHour?: number;
  /**
   * The GUEST price for time no zone covers (#833), or null for «not set» —
   * then a guest cannot book those minutes. Never read as 0.
   */
  guestDefaultPricePerHour?: number | null;
  /** Booking config — used for the active holiday set (holidayCountry/extra/removed). */
  config?: any;
  /** Closes the modal without saving. */
  onClose: () => void;
  /**
   * Persists the edited zones, the default price and the guest default price:
   * onSave(draftZones, defaultPrice, guestDefaultPrice). The guest default is
   * `null` when it is not set (#833).
   */
  onSave: (zones: any[], defaultPrice: number, guestDefaultPrice: number | null) => void;
}

/** Price-zone editor modal (primetime, holiday zones, …); extracted verbatim from the original artifact (legacy/App.jsx). */
export function BookingZoneEditor({
  zones = [],
  defaultPricePerHour = 0,
  guestDefaultPricePerHour = null,
  config = null,
  onClose,
  onSave,
}: BookingZoneEditorProps) {
  const t = useT();
  const uid = safeId('booking-zones', useId());
  const ids = {
    title: `${uid}-title`,
    lead: `${uid}-lead`,
    holidays: `${uid}-holidays`,
    holidaysLead: `${uid}-holidays-lead`,
    defaultPrice: `${uid}-default-price`,
    defaultPriceHelp: `${uid}-default-price-help`,
    guestDefaultPrice: `${uid}-guest-default-price`,
    guestDefaultPriceHelp: `${uid}-guest-default-price-help`,
    perHour: `${uid}-per-hour`,
    // Per zone: a zone's fields repeat once per zone, so its id is in each.
    zone: (zoneId: string, field: string) => `${uid}-zone-${zoneId}-${field}`,
  };
  // Local draft — we only commit when the admin clicks «Lagre alle endringer»
  const [draft, setDraft] = useState(() =>
    zones.map((z) => ({
      ...z,
      timeRanges: [...(z.timeRanges || [])],
      daysOfWeek: [...(z.daysOfWeek || [])],
    })),
  );
  const [defaultPrice, setDefaultPrice] = useState(defaultPricePerHour);
  // THE GUEST DEFAULT (#833). `null` is «not set» and is kept apart from 0, which
  // is a real price — free guest booking, allowed with a warning (decision 7).
  const [guestDefault, setGuestDefault] = useState<number | null>(() =>
    guestPriceOf(guestDefaultPricePerHour),
  );
  /** A guest-price field's text → the stored value: empty is «not set». */
  const guestPriceFromInput = (raw: string): number | null =>
    raw.trim() === '' ? null : Math.max(0, Number(raw) || 0);
  // Any guest price of 0 kr — on a zone or the default — earns the warning.
  const guestFree =
    guestDefault === 0 || draft.some((z) => guestPriceOf(z.guestPricePerHour) === 0);
  // Inline delete confirmation per zone (confirm() does not work in an iframe).
  const [confirmDeleteZone, setConfirmDeleteZone] = useState(null);
  // PRICE-ZONE MINIMIZATION: each zone is minimized by default; the admin expands it to
  // edit days/time ranges/holidays (the recurring rules).
  const [expandedZones, setExpandedZones] = useState(() => new Set());
  const toggleZone = (id) =>
    setExpandedZones((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  // Show/hide popup with which dates are holidays (active set).
  const [showHolidayList, setShowHolidayList] = useState(false);
  const holidayCountry = (config && config.holidayCountry) || 'no';
  // Upcoming holidays in the active set (with names), for "Vis dager".
  // Weekday shorthands for DISPLAY only — the ISO date is what is stored and
  // compared (docs/architecture/DOMAIN_RULES.md §4), never this label.
  const weekdayShort = useMemo(
    () => [
      t('booking.weekday.short.sun'),
      t('booking.weekday.short.mon'),
      t('booking.weekday.short.tue'),
      t('booking.weekday.short.wed'),
      t('booking.weekday.short.thu'),
      t('booking.weekday.short.fri'),
      t('booking.weekday.short.sat'),
    ],
    [t],
  );
  const upcomingHolidays = useMemo(() => {
    const set = getHolidaySet(config || { holidayCountry });
    const todayStr = localDateOf(Date.now()); // LOCAL today (DOMAIN_RULES §4, #939)
    return [...set]
      .filter((d) => d >= todayStr)
      .sort()
      .slice(0, 30)
      .map((iso) => {
        const d = new Date(iso + 'T00:00:00');
        const wd = weekdayShort[d.getDay()];
        return {
          iso,
          name: holidayName(iso, holidayCountry),
          label: `${wd} ${d.getDate()}.${d.getMonth() + 1}`,
        };
      });
  }, [config, holidayCountry, weekdayShort]);

  const addZone = () => {
    const nextId = `zone_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setExpandedZones((s) => {
      const n = new Set(s);
      n.add(nextId);
      return n;
    });
    setDraft((d) => [
      ...d,
      {
        id: nextId,
        name: 'Ny sone',
        pricePerHour: 300,
        priceMode: 'replace',
        daysOfWeek: [],
        includeHolidays: false,
        onlyHolidays: false,
        excludeHolidays: false,
        timeRanges: [],
        priority: Math.max(...d.map((z) => z.priority || 0), 0) + 10,
      },
    ]);
  };
  const deleteZone = (id) => {
    setDraft((d) => d.filter((z) => z.id !== id));
  };
  const updateZone = (id, patch) => {
    setDraft((d) => d.map((z) => (z.id === id ? { ...z, ...patch } : z)));
  };
  const toggleDay = (id, day) => {
    setDraft((d) =>
      d.map((z) => {
        if (z.id !== id) return z;
        const days = z.daysOfWeek || [];
        return {
          ...z,
          daysOfWeek: days.includes(day) ? days.filter((x) => x !== day) : [...days, day],
        };
      }),
    );
  };
  const addTimeRange = (id) => {
    updateZone(id, {
      timeRanges: [
        ...(draft.find((z) => z.id === id)?.timeRanges || []),
        { start: '17:00', end: '22:00' },
      ],
    });
  };
  const removeTimeRange = (id, idx) => {
    const z = draft.find((x) => x.id === id);
    if (!z) return;
    updateZone(id, { timeRanges: z.timeRanges.filter((_, i) => i !== idx) });
  };
  const updateTimeRange = (id, idx, patch) => {
    const z = draft.find((x) => x.id === id);
    if (!z) return;
    updateZone(id, {
      timeRanges: z.timeRanges.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });
  };

  const sorted = [...draft].sort((a, b) => (b.priority || 0) - (a.priority || 0));

  // A `Dialog` (#900): named by its own title, focus moved in, kept inside and
  // given back to the opener, Escape closes as the backdrop does. The inline
  // styles are the hand-built overlay's, moved verbatim.
  return (
    <>
      {/* Popup: which dates are holidays (active set). A SIBLING of the editor
          rather than inside its backdrop, so it is simply the innermost open
          dialog: Escape closes it and not the editor, and focus goes back to
          the «Vis dager» that opened it (#900). */}
      {showHolidayList && (
        <Dialog
          labelledBy={ids.holidays}
          describedBy={ids.holidaysLead}
          onClose={() => setShowHolidayList(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15,22,17,0.6)',
          }}
          className=""
          style={{
            background: '#fff',
            maxWidth: 380,
            width: '100%',
            maxHeight: 'calc(100dvh - 120px)',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.45)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '14px 20px',
              borderBottom: '1px solid #e7eae3',
              background: '#fafbf7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h3 id={ids.holidays} className="font-display text-base font-semibold">
              {t('booking.zones.holidayPopup.title', {
                country: (holidayCountry || 'no').toUpperCase(),
              })}
            </h3>
            <button
              type="button"
              onClick={() => setShowHolidayList(false)}
              aria-label={t('booking.close')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#4a544c',
                padding: 4,
              }}
            >
              <X size={16} />
            </button>
          </div>
          <div style={{ padding: '12px 20px', overflowY: 'auto' }} className="space-y-1">
            <p id={ids.holidaysLead} className="text-[11px] text-ink-3 mb-2">
              {t('booking.zones.holidayPopup.lead')}
            </p>
            {upcomingHolidays.length === 0 ? (
              <div className="text-[11px] text-ink-3 italic">
                {t('booking.zones.holidayPopup.empty')}
              </div>
            ) : (
              upcomingHolidays.map((h) => (
                <div
                  key={h.iso}
                  className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-bg-soft/30 border border-line"
                >
                  <span className="text-ink-1 font-medium">
                    {h.name || t('booking.zones.holidayPopup.fallbackName')}
                  </span>
                  <span className="text-ink-3 font-mono">{h.label}</span>
                </div>
              ))
            )}
          </div>
        </Dialog>
      )}
      <Dialog
        labelledBy={ids.title}
        describedBy={ids.lead}
        onClose={onClose}
        showClose={false}
        backdropClassName=""
        backdropStyle={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          background: 'rgba(15, 22, 17, 0.55)',
        }}
        className=""
        style={{
          background: '#ffffff',
          maxWidth: 720,
          width: '100%',
          maxHeight: 'calc(100dvh - 100px)',
          marginTop: 80,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
          border: '1px solid #e7eae3',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e7eae3',
            background: '#fafbf7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div>
            <h3 id={ids.title} className="font-display text-lg font-semibold leading-tight">
              {t('booking.zones.title')}
            </h3>
            <p id={ids.lead} className="text-[11px] text-ink-3 mt-0.5">
              {t('booking.zones.lead')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('booking.close')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#4a544c',
              padding: 4,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: '1 1 auto' }} className="space-y-3">
          {/* Default/base price — applies to time without a matching zone */}
          <div className="border border-line rounded-lg p-3 bg-emerald-50/40">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div id={ids.defaultPrice} className="text-sm font-semibold text-ink-1">
                  {t('booking.zones.defaultPrice')}
                </div>
                <div id={ids.defaultPriceHelp} className="text-[11px] text-ink-3">
                  {t('booking.zones.defaultPriceHelp')}
                  {defaultPrice === 0 && (
                    <span className="text-emerald-700 font-semibold">
                      {' '}
                      {t('booking.zones.defaultPriceFree')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setDefaultPrice(0)}
                  aria-pressed={defaultPrice === 0}
                  className={`text-[11px] font-semibold px-2 py-1.5 rounded-lg transition-colors ${defaultPrice === 0 ? 'bg-emerald-700 text-white' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line-strong'}`}
                >
                  {t('booking.zones.free')}
                </button>
                <input
                  type="number"
                  min={0}
                  step={10}
                  className="input text-sm w-20 text-right"
                  aria-labelledby={`${ids.defaultPrice} ${ids.perHour}`}
                  aria-describedby={ids.defaultPriceHelp}
                  value={defaultPrice}
                  onChange={(e) => setDefaultPrice(Math.max(0, Number(e.target.value) || 0))}
                />
                <span id={ids.perHour} className="text-xs text-ink-3">
                  {t('booking.zones.perHour')}
                </span>
              </div>
            </div>
          </div>

          {/* GUEST PRICES (#833, owner decision 2026-11-04) — what a player who is
              not a member here pays. Per zone below, and this one for minutes no
              zone covers. Empty is «not set»: a guest cannot book that time, and
              it NEVER falls back to the member price. Not in force until guest
              booking opens (the next slice), and the box says so. */}
          <div
            className="border border-line rounded-lg p-3 bg-sky-50/40 space-y-2"
            data-testid="booking-zones-guest"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div id={ids.guestDefaultPrice} className="text-sm font-semibold text-ink-1">
                  {t('booking.zones.guestDefaultPrice')}
                </div>
                <div id={ids.guestDefaultPriceHelp} className="text-[11px] text-ink-3">
                  {t('booking.zones.guestDefaultPriceHelp')}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <input
                  type="number"
                  min={0}
                  step={10}
                  className="input text-sm w-20 text-right"
                  aria-labelledby={`${ids.guestDefaultPrice} ${ids.perHour}`}
                  aria-describedby={ids.guestDefaultPriceHelp}
                  placeholder={t('booking.zones.guestPriceUnset')}
                  value={guestDefault ?? ''}
                  onChange={(e) => setGuestDefault(guestPriceFromInput(e.target.value))}
                />
                <span className="text-xs text-ink-3">{t('booking.zones.perHour')}</span>
              </div>
            </div>
            <p className="text-[11px] text-ink-3 leading-relaxed">
              {t('booking.zones.guestNotOpenYet')}
            </p>
            {guestFree && (
              <p
                className="text-[11px] font-semibold text-amber-900 bg-amber-50 rounded-md px-2 py-1.5"
                role="status"
                data-testid="booking-zones-guest-free-warning"
              >
                {t('booking.zones.guestFreeWarning')}
              </p>
            )}
          </div>

          {/* Quick view of active holidays */}
          <div className="border border-line rounded-lg overflow-hidden">
            <button
              onClick={() => setShowHolidayList((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-bg-soft/40 transition-colors"
            >
              <span className="text-xs font-semibold text-ink-1">
                {t('booking.zones.holidaysToggle', {
                  country: (config?.holidayCountry || 'no').toUpperCase(),
                })}
              </span>
              <span className="text-ink-3 text-xs">
                {t(showHolidayList ? 'booking.zones.hide' : 'booking.zones.showDays')}
              </span>
            </button>
            {showHolidayList &&
              (() => {
                const set = getHolidaySet(config || {});
                const todayStr = localDateOf(Date.now()); // LOCAL today (DOMAIN_RULES §4, #939)
                const upcoming = [...set]
                  .filter((d) => d >= todayStr)
                  .sort()
                  .slice(0, 20);
                const fmt = (iso) => {
                  const d = new Date(iso + 'T00:00:00');
                  const wd = weekdayShort[d.getDay()];
                  return `${wd} ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
                };
                return (
                  <div className="px-3 py-2 border-t border-line max-h-48 overflow-y-auto space-y-0.5">
                    <p className="text-[10px] text-ink-3 mb-1">
                      {t('booking.zones.holidaysInlineLead')}
                    </p>
                    {upcoming.length === 0 && (
                      <p className="text-[11px] text-ink-3 italic">
                        {t('booking.zones.holidaysInlineEmpty')}
                      </p>
                    )}
                    {upcoming.map((iso) => {
                      const navn = holidayName(iso, config?.holidayCountry || 'no');
                      return (
                        <div key={iso} className="flex items-baseline gap-2 text-[11px]">
                          <span className="text-ink-1 font-medium">
                            {navn || t('booking.zones.customHoliday')}
                          </span>
                          <span className="text-ink-3">{fmt(iso)}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
          </div>
          {sorted.length === 0 && (
            <div className="text-center py-8 text-ink-3 text-sm italic">
              {t('booking.zones.empty')}
            </div>
          )}
          {sorted.map((z) => {
            const isExpanded = expandedZones.has(z.id);
            return (
              <div key={z.id} className="border border-line rounded-lg p-3 bg-bg-soft/30">
                <div className="flex items-end gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => toggleZone(z.id)}
                    className="flex-shrink-0 text-ink-3 hover:text-ink-1 pb-2"
                    title={t(
                      isExpanded ? 'booking.zones.collapseZone' : 'booking.zones.expandZone',
                    )}
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor={ids.zone(z.id, 'name')}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                    >
                      {t('booking.zones.zoneName')}
                    </label>
                    <input
                      id={ids.zone(z.id, 'name')}
                      type="text"
                      className="input w-full text-sm font-semibold"
                      value={z.name}
                      onChange={(e) => updateZone(z.id, { name: e.target.value })}
                      placeholder={t('booking.zones.zoneNamePlaceholder')}
                    />
                  </div>
                  <div className="flex-shrink-0" style={{ width: '6.25rem' }}>
                    <label
                      htmlFor={ids.zone(z.id, 'price')}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                    >
                      {t('booking.zones.price')}
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        id={ids.zone(z.id, 'price')}
                        type="number"
                        className="input text-sm text-right"
                        style={{ width: '4.25rem' }}
                        value={z.pricePerHour}
                        onChange={(e) => updateZone(z.id, { pricePerHour: Number(e.target.value) })}
                        min="0"
                      />
                      <span className="text-[10px] text-ink-3 whitespace-nowrap">
                        {t('booking.zones.perHour')}
                      </span>
                    </div>
                  </div>
                  {/* The zone's GUEST price (#833). Empty is «not set»: guests
                      cannot book this zone's time — never the member price. */}
                  <div className="flex-shrink-0" style={{ width: '6.25rem' }}>
                    <label
                      htmlFor={ids.zone(z.id, 'guestPrice')}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      title={t('booking.zones.guestPriceHelp')}
                    >
                      {t('booking.zones.guestPrice')}
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        id={ids.zone(z.id, 'guestPrice')}
                        type="number"
                        className="input text-sm text-right"
                        style={{ width: '4.25rem' }}
                        placeholder={t('booking.zones.guestPriceUnset')}
                        title={t('booking.zones.guestPriceHelp')}
                        value={guestPriceOf(z.guestPricePerHour) ?? ''}
                        onChange={(e) =>
                          updateZone(z.id, {
                            guestPricePerHour: guestPriceFromInput(e.target.value) ?? undefined,
                          })
                        }
                        min="0"
                      />
                      <span className="text-[10px] text-ink-3 whitespace-nowrap">
                        {t('booking.zones.perHour')}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <label
                      htmlFor={ids.zone(z.id, 'priority')}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      title={t('booking.zones.priorityHelp')}
                    >
                      {t('booking.zones.priority')}
                    </label>
                    <input
                      id={ids.zone(z.id, 'priority')}
                      type="number"
                      className="input text-sm text-center"
                      style={{ width: '3rem' }}
                      value={z.priority || 0}
                      onChange={(e) => updateZone(z.id, { priority: Number(e.target.value) })}
                      title={t('booking.zones.priorityInputHelp')}
                    />
                  </div>
                  {confirmDeleteZone === z.id ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {
                          deleteZone(z.id);
                          setConfirmDeleteZone(null);
                        }}
                        className="text-xs font-semibold px-2 py-2 rounded"
                        style={{ background: '#dc2626', color: '#fff' }}
                        title={t('booking.zones.confirmDelete')}
                      >
                        {t('booking.zones.delete')}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteZone(null)}
                        className="text-xs px-2 py-2 rounded text-ink-3 hover:text-ink-1"
                      >
                        {t('booking.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteZone(z.id)}
                      className="px-2 py-2 rounded text-sm flex-shrink-0"
                      style={{ color: '#dc2626' }}
                      title={t('booking.zones.deleteZone')}
                      aria-label={t('booking.zones.deleteZone')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Minimized summary — shown when the zone is minimized */}
                {!isExpanded &&
                  (() => {
                    const dayKeys = z.daysOfWeek || [];
                    const days = z.onlyHolidays
                      ? t('booking.zones.summary.onlyHolidays')
                      : dayKeys.length
                        ? dayKeys.map((d) => DAY_OF_WEEK_LABELS[d]).join(' ')
                        : t('booking.zones.summary.allDays');
                    const times = (z.timeRanges || []).length
                      ? z.timeRanges.map((r) => `${r.start}–${r.end}`).join(', ')
                      : t('booking.zones.summary.allDay');
                    const mode = t(
                      z.priceMode === 'add'
                        ? 'booking.zones.summary.modeAdd'
                        : 'booking.zones.summary.modeReplace',
                    );
                    return (
                      <button
                        type="button"
                        onClick={() => toggleZone(z.id)}
                        className="block w-full text-left text-[11px] text-ink-3 truncate hover:text-ink-2"
                      >
                        {days} · {times} · {mode}
                      </button>
                    );
                  })()}

                {isExpanded && (
                  <>
                    {/* Price mode: replace or add to the default price */}
                    <div className="mb-2">
                      <div
                        id={ids.zone(z.id, 'price-mode')}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      >
                        {t('booking.zones.priceMode')}
                      </div>
                      <div
                        className="flex gap-1.5"
                        role="group"
                        aria-labelledby={ids.zone(z.id, 'price-mode')}
                      >
                        <button
                          onClick={() => updateZone(z.id, { priceMode: 'replace' })}
                          aria-pressed={(z.priceMode || 'replace') === 'replace'}
                          className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${(z.priceMode || 'replace') === 'replace' ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line-strong'}`}
                        >
                          {t('booking.zones.priceModeReplace')}
                        </button>
                        <button
                          onClick={() => updateZone(z.id, { priceMode: 'add' })}
                          aria-pressed={z.priceMode === 'add'}
                          className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${z.priceMode === 'add' ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line-strong'}`}
                        >
                          {t('booking.zones.priceModeAdd')}
                        </button>
                      </div>
                      <p className="text-[10px] text-ink-3 mt-1">
                        {t(
                          (z.priceMode || 'replace') === 'replace'
                            ? 'booking.zones.priceModeReplaceHelp'
                            : 'booking.zones.priceModeAddHelp',
                          { price: String(z.pricePerHour) },
                        )}
                      </p>
                    </div>

                    {/* Weekdays */}
                    <div className="mb-2">
                      <div
                        id={ids.zone(z.id, 'weekdays')}
                        className="text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      >
                        {t('booking.zones.weekdays')}
                      </div>
                      <div
                        className="flex gap-1 flex-wrap"
                        role="group"
                        aria-labelledby={ids.zone(z.id, 'weekdays')}
                      >
                        {DAY_OF_WEEK_KEYS.filter((k) => k !== 'sun' || true).map((day) => {
                          const sel = (z.daysOfWeek || []).includes(day);
                          return (
                            <button
                              key={day}
                              onClick={() => toggleDay(z.id, day)}
                              disabled={z.onlyHolidays}
                              aria-pressed={sel}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                z.onlyHolidays
                                  ? 'opacity-40 cursor-not-allowed bg-paper border-line-strong text-ink-3'
                                  : sel
                                    ? 'bg-emerald-700 border-emerald-800 text-white'
                                    : 'bg-paper border-line-strong text-ink-3 hover:border-ink'
                              }`}
                            >
                              {DAY_OF_WEEK_LABELS[day]}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Holiday handling */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          id={ids.zone(z.id, 'holidays')}
                          className="text-[10px] font-mono uppercase tracking-wider text-ink-3"
                        >
                          {t('booking.zones.holidays')}
                        </span>
                        <button
                          onClick={() => setShowHolidayList(true)}
                          className="text-[10px] text-emerald-700 hover:underline font-semibold"
                        >
                          {t('booking.zones.showDaysCountry', {
                            country: (holidayCountry || 'no').toUpperCase(),
                          })}
                        </button>
                      </div>
                      {z.onlyHolidays ? (
                        <p className="text-[11px] text-ink-3 mb-1.5 px-2 py-1 rounded bg-bg-soft/40">
                          {t('booking.zones.onlyHolidaysNoteBefore')}{' '}
                          <strong>{t('booking.zones.onlyHolidaysNoteStrong')}</strong>{' '}
                          {t('booking.zones.onlyHolidaysNoteAfter')}
                        </p>
                      ) : (
                        <>
                          <p className="text-[11px] text-ink-3 mb-1">
                            {t('booking.zones.holidayWhen')}
                          </p>
                          <div
                            className="flex gap-1.5 mb-1.5 flex-wrap"
                            role="group"
                            aria-labelledby={ids.zone(z.id, 'holidays')}
                          >
                            <button
                              onClick={() =>
                                updateZone(z.id, { excludeHolidays: false, includeHolidays: false })
                              }
                              aria-pressed={!z.excludeHolidays}
                              className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${!z.excludeHolidays ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line-strong'}`}
                            >
                              {t('booking.zones.holidayInclude')}
                            </button>
                            <button
                              onClick={() =>
                                updateZone(z.id, { excludeHolidays: true, includeHolidays: false })
                              }
                              aria-pressed={!!z.excludeHolidays}
                              className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${z.excludeHolidays ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line-strong'}`}
                            >
                              {t('booking.zones.holidaySkip')}
                            </button>
                          </div>
                        </>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer mt-1">
                        <input
                          type="checkbox"
                          checked={!!z.onlyHolidays}
                          onChange={(e) =>
                            updateZone(z.id, {
                              onlyHolidays: e.target.checked,
                              includeHolidays: false,
                              excludeHolidays: false,
                            })
                          }
                          className="w-3.5 h-3.5 accent-emerald-600"
                        />
                        <span className="text-xs text-ink-2">
                          {t('booking.zones.onlyHolidaysCheckbox')}
                        </span>
                      </label>
                      {z.onlyHolidays && (
                        <p className="text-[10px] text-ink-3 mt-1 pl-5">
                          {t('booking.zones.tipBefore')}{' '}
                          <strong>{t('booking.zones.tipStrong')}</strong>{' '}
                          {t('booking.zones.tipAfter')}
                        </p>
                      )}
                    </div>

                    {/* Time ranges */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span
                          id={ids.zone(z.id, 'ranges')}
                          className="text-[10px] font-mono uppercase tracking-wider text-ink-3"
                        >
                          {t('booking.zones.timeRanges')}
                        </span>
                        <button
                          onClick={() => addTimeRange(z.id)}
                          className="text-xs text-emerald-700 hover:underline"
                        >
                          {t('booking.zones.addTimeRange')}
                        </button>
                      </div>
                      {(z.timeRanges || []).length === 0 ? (
                        <div
                          className="text-[11px] px-2.5 py-1.5 rounded-lg"
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                          }}
                        >
                          {t('booking.zones.noRangeBefore')}{' '}
                          <strong>{t('booking.zones.noRangeStrong')}</strong>{' '}
                          {t('booking.zones.noRangeAfter')}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {z.timeRanges.map((r, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2"
                              role="group"
                              aria-labelledby={ids.zone(z.id, 'ranges')}
                            >
                              <input
                                type="time"
                                className="input text-sm"
                                aria-label={t('booking.zones.rangeFrom')}
                                value={r.start}
                                onChange={(e) =>
                                  updateTimeRange(z.id, idx, { start: e.target.value })
                                }
                              />
                              <span className="text-xs text-ink-3">–</span>
                              <input
                                type="time"
                                className="input text-sm"
                                aria-label={t('booking.zones.rangeTo')}
                                value={r.end}
                                onChange={(e) =>
                                  updateTimeRange(z.id, idx, { end: e.target.value })
                                }
                              />
                              <button
                                onClick={() => removeTimeRange(z.id, idx)}
                                aria-label={t('booking.zones.removeRange')}
                                className="text-rose hover:bg-rose-soft px-1.5 py-0.5 rounded text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <button
            onClick={addZone}
            className="w-full border-2 border-dashed border-emerald-300 text-emerald-700 rounded-lg py-2.5 text-sm font-semibold hover:bg-emerald-50 transition-colors"
          >
            {t('booking.zones.addZone')}
          </button>
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e7eae3',
            background: '#fafbf7',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button onClick={onClose} className="btn-outline text-sm">
            {t('booking.cancel')}
          </button>
          <button
            onClick={() => onSave(draft, defaultPrice, guestDefault)}
            className="btn-lime text-sm"
          >
            {t('booking.zones.saveAll')}
          </button>
        </div>
      </Dialog>
    </>
  );
}
